import React, { useMemo, useRef, useState } from 'react';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LinkIcon from '@mui/icons-material/Link';
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import {
  PamGrid, formatDateTimeForDisplay, useGridConfigs, useNotification,
} from '@1823-partners/core';
import { SetFilterModule } from 'ag-grid-enterprise';

import api from '../../api';
import { buildRemittanceRunLink } from '../../utils/remittanceDeepLink';

const getRunRowId = (data) => String(data.RUN_ID);

/**
 * One compact row per run. The run is only a selector for the reconciliation below, so the grid
 * keeps the identity, timing and two actionable input-loss counts rather than restating all of the
 * reconciliation totals and provenance.
 *
 * There is no environment column. A row read from DEV.RECON.CML_REMIT_RUNS is a DEV run -- the
 * database is the address of the row, not a fact about it -- so the server stopped recording it
 * rather than have the grid restate the connection it was just fetched over.
 */
const RunHistoryGrid = ({ runs, isLoading, onSelectRun, selectedRunId, height = 220 }) => {
  const { showNotification } = useNotification();
  const gridRef = useRef(null);
  const [runsExpanded, setRunsExpanded] = useState(false);
  const selectedRun = runs.find((run) => run.RUN_ID === selectedRunId);
  const selectedRunSummary = selectedRun
    ? [
      selectedRun.PERIOD,
      selectedRun.RUN_STATUS,
      selectedRun.FINISHED_AT
        ? formatDateTimeForDisplay(selectedRun.FINISHED_AT)
        : null,
    ].filter(Boolean).join(' · ')
    : `${runs.length} run${runs.length === 1 ? '' : 's'}`;

  // Keep the input-loss counts at the end: they qualify the selected result rather than identify
  // it, and their non-zero state is explained more prominently by the warning above this selector.
  const columnDefs = useMemo(
    () => [
      { field: 'PERIOD', headerName: 'Period', minWidth: 110, filter: 'agSetColumnFilter' },
      {
        field: 'RUN_STATUS',
        headerName: 'Status',
        minWidth: 155,
        filter: 'agSetColumnFilter',
        cellClassRules: {
          'remit-status-refused': (p) => p.value === 'provisional_required',
          'remit-status-provisional': (p) => p.value === 'provisional',
        },
      },
      {
        field: 'FINISHED_AT',
        headerName: 'Run at',
        minWidth: 175,
        valueFormatter: (p) => (p.value ? formatDateTimeForDisplay(p.value) : ''),
      },
      { field: 'UNRECOGNIZED_FILES', headerName: 'Unrecognized files', type: 'numericColumn', minWidth: 155 },
      { field: 'UNMATCHED_ROWS', headerName: 'Unmatched rows', type: 'numericColumn', minWidth: 150 },
    ],
    []
  );

  const gridConfigs = useGridConfigs(api, {
    app: 'recon',
    gridId: 'remittance-runs',
    gridApiRef: gridRef,
  });

  const copySelectedRunLink = async () => {
    if (!selectedRun) return;
    const link = buildRemittanceRunLink(window.location.href, selectedRun.RUN_ID);
    try {
      if (!window.navigator.clipboard?.writeText) throw new Error('Clipboard API unavailable');
      await window.navigator.clipboard.writeText(link);
      showNotification(`Link copied for ${selectedRun.PERIOD}.`, 'success');
    } catch (err) {
      showNotification('Could not copy the run link. Your browser denied clipboard access.', 'error');
    }
  };

  return (
    <Box sx={{ display: 'contents' }}>
      <Box
        sx={{
          gridArea: 'runsToggle',
          minWidth: 0,
          mt: { xs: 1.5, md: 0 },
          display: 'flex',
          gap: 0.5,
          alignItems: 'center',
        }}
      >
        <Button
          variant="outlined"
          color="inherit"
          fullWidth
          onClick={() => setRunsExpanded((expanded) => !expanded)}
          aria-expanded={runsExpanded}
          aria-controls="remittance-run-history"
          endIcon={runsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          data-testid="remittance-runs-toggle"
          sx={{ justifyContent: 'space-between', textTransform: 'none', px: 1.5, py: 0.75 }}
        >
          <Box component="span" sx={{ textAlign: 'left' }}>
            <Typography component="span" variant="subtitle2" fontWeight={700}>
              Reconciliation runs
            </Typography>
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              {isLoading ? 'Loading…' : selectedRunSummary}
            </Typography>
          </Box>
        </Button>
        <Tooltip title={selectedRun ? 'Copy link to this run' : 'Select a run to copy its link'}>
          <span>
            <IconButton
              aria-label="Copy link to selected run"
              onClick={copySelectedRunLink}
              disabled={!selectedRun}
              size="small"
            >
              <LinkIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {runsExpanded && (
        <Box
          id="remittance-run-history"
          data-testid="remittance-runs-content"
          sx={{ gridArea: 'runsContent', minWidth: 0, mt: 1.5 }}
        >
          <PamGrid
            ref={gridRef}
            rowData={runs}
            columnDefs={columnDefs}
            getRowId={getRunRowId}
            height={`${height}px`}
            showToolbar
            title="Reconciliation runs"
            showExport
            exportConfig={{
              fileName: `CMLRemittanceRuns_${new Date().toISOString().split('T')[0]}.xlsx`,
              sheetName: 'Runs',
            }}
            onGridReady={gridConfigs.onGridReady}
            onFilterChanged={gridConfigs.onGridStateChanged}
            onRowClicked={(event) => {
              onSelectRun?.(event.data);
              setRunsExpanded(false);
            }}
            rowClassRules={{ 'remit-row-selected': (p) => p.data?.RUN_ID === selectedRunId }}
            isLoading={isLoading}
            loadingMessage="Loading run history..."
            modules={[SetFilterModule]}
          />
        </Box>
      )}
    </Box>
  );
};

export default RunHistoryGrid;
